import { useScope } from "~/hooks/useScope";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { Flex } from "~/components/Flex";
import {
  ExceptionReviewAction,
  ListExceptionGroupsDocument,
  ExceptionStatus,
  useExtendExceptionMutation,
} from "~/operations";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import {
  AccessTimeOutlined,
  DeleteOutline,
  DoNotDisturbOffOutlined,
  EditOutlined,
} from "@mui/icons-material";
import { MouseEvent, useState } from "react";
import { useDialogs } from "@toolpad/core";
import {
  RemoveExceptionsGroupDialog,
  RemoveExceptionsGroupDialogPayload,
} from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { useSpaceSettings } from "~/pages/space-settings/hooks/useSpaceSettings";
import { ExceptionButtonSelfApprovalTooltipWrapper } from "./ExceptionButtonSelfApprovalTooltipWrapper";
import { ExtendExceptionDialog } from "~/components/exceptions/ExtendExceptionDialog";
import { validUntilDateString } from "~/components/exceptions/use-exceptions";
import { enqueueSnackbar } from "notistack";
import { useViewer } from "~/providers/viewer";

interface Props {
  exceptionGroup: ExceptionGroup;
  onReviewActionChange?: (
    value: ExceptionGroup,
    action: ExceptionReviewAction,
  ) => void;
  isLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  removeExceptionGroupPayload?: RemoveExceptionsGroupDialogPayload;
  refetch?: () => void;
  enabledExtension?: boolean;
}

const ExceptionButtons = ({
  exceptionGroup,
  onReviewActionChange,
  isLoading,
  loadingAction,
  removeExceptionGroupPayload,
  refetch,
  enabledExtension,
}: Props) => {
  const isApproved = exceptionGroup.status === ExceptionStatus.Approved;
  const isRejected = exceptionGroup.status === ExceptionStatus.Rejected;
  const isReviewed = exceptionGroup.status !== ExceptionStatus.New;
  const isPendingExtension =
    exceptionGroup.status === ExceptionStatus.NeedsExtension;
  const isRemoved = exceptionGroup.status === ExceptionStatus.Removed;
  const isExpired = exceptionGroup.status === ExceptionStatus.Expired;
  const isIndefinite = !Boolean(exceptionGroup.validUntil);
  const canExtendException =
    isApproved && !isRemoved && !isIndefinite && !isExpired && enabledExtension;

  const [extendException] = useExtendExceptionMutation();

  const { spaceScope } = useScope();
  const { exceptionsAllowSelfApproval } = spaceScope
    ? useSpaceSettings({
        space: spaceScope,
      })
    : { exceptionsAllowSelfApproval: false };
  const { viewer } = useViewer();
  const createdBySelf =
    exceptionGroup.author?.mrn === viewer?.mrn ||
    exceptionGroup.author?.email === viewer?.email;
  const canApplyExceptionUpdate = useCanApplyExceptionMutation(spaceScope);
  const canDeleteOwnPendingException = Boolean(
    exceptionGroup.status === ExceptionStatus.New &&
      createdBySelf &&
      !exceptionsAllowSelfApproval &&
      removeExceptionGroupPayload,
  );

  const dialogs = useDialogs();

  const handleDeleteExceptionsGroupDialogOpen = (
    dialogPayload?: Partial<
      Omit<RemoveExceptionsGroupDialogPayload, "onRemove" | "exceptionMrns">
    >,
  ) => {
    if (removeExceptionGroupPayload) {
      dialogs.open(RemoveExceptionsGroupDialog, {
        ...removeExceptionGroupPayload,
        ...dialogPayload,
      });
    }
  };

  const handleDeleteOwnPendingException = () => {
    handleDeleteExceptionsGroupDialogOpen({
      title: "DELETE EXCEPTION REQUEST?",
      message:
        "Are you sure you want to delete this exception request? This will remove the pending request without rejecting it.",
      confirmButtonText: "Yes, Delete the Exception Request",
      cancelButtonText: "No, keep the exception request",
      successMessage: "Exception request deleted successfully",
      errorMessage: "Failed to delete exception request",
    });
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenRequestExtensionDialog = () => {
    dialogs.open(ExtendExceptionDialog, {
      onSave: async (params) => {
        try {
          await extendException({
            variables: {
              input: {
                exceptionMrn: exceptionGroup.mrn,
                justification: params.justification,
                validUntil:
                  validUntilDateString(
                    params.validUntil,
                    params.customValidUntil,
                  ) || "",
              },
            },
            refetchQueries: [ListExceptionGroupsDocument],
          });

          enqueueSnackbar("Exception extension requested successfully", {
            variant: "success",
          });

          refetch?.();
        } catch (error: any) {
          const message =
            error?.message || "Failed to request exception extension";
          enqueueSnackbar(message, { variant: "error" });
        }
      },
      targetData: {
        targetTitle: exceptionGroup.title,
      },
    });
  };

  if (isRemoved) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Removed
        </Typography>
      </Stack>
    );
  }

  if (isExpired) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Expired
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      {isReviewed && isApproved && (
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent="flex-end"
          flexDirection="row-reverse"
        >
          {canApplyExceptionUpdate && removeExceptionGroupPayload && (
            <Grid order={{ xs: 2, md: 1 }}>
              <Tooltip title="Modify Exception">
                <IconButton
                  data-test-id="exception-modify-menu-button"
                  color="default"
                  onClick={handleOpenMenu}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {canExtendException && (
                  <MenuItem
                    data-test-id="exception-request-time-extension"
                    onClick={() => {
                      handleCloseMenu();
                      handleOpenRequestExtensionDialog();
                    }}
                  >
                    <ListItemIcon>
                      <AccessTimeOutlined fontSize="small" />
                    </ListItemIcon>
                    Request time extension
                  </MenuItem>
                )}
                <MenuItem
                  data-test-id="exception-delete-button"
                  sx={{ color: "error.light" }}
                  onClick={() => {
                    handleCloseMenu();
                    handleDeleteExceptionsGroupDialogOpen();
                  }}
                >
                  <ListItemIcon>
                    <DoNotDisturbOffOutlined
                      fontSize="small"
                      sx={{ color: "error.light" }}
                    />
                  </ListItemIcon>
                  Remove exception and enable
                </MenuItem>
              </Menu>
            </Grid>
          )}
          <Grid order={{ xs: 1, md: 2 }} size="auto">
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              <Typography
                data-test-id="exception-approved-tag"
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "uppercase", fontWeight: 500 }}
              >
                Approved
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      )}
      {isReviewed && isRejected && (
        <Stack direction="row" spacing={2} alignItems="center">
          <CloseIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textTransform: "uppercase", fontWeight: 500 }}
          >
            Rejected
          </Typography>
        </Stack>
      )}
      {(!isReviewed || isPendingExtension) &&
        !isExpired &&
        (canApplyExceptionUpdate || canDeleteOwnPendingException) && (
          <Flex gap={3}>
            {canDeleteOwnPendingException ? (
              <Button
                variant="outlined"
                color="error"
                data-test-id="exception-delete-own-pending-button"
                startIcon={<DeleteOutline fontSize="inherit" />}
                disabled={isLoading}
                onClick={handleDeleteOwnPendingException}
              >
                Delete request
              </Button>
            ) : (
              canApplyExceptionUpdate &&
              onReviewActionChange && (
                <Button
                  data-test-id="exception-reject-button"
                  variant="outlined"
                  color="error"
                  startIcon={
                    isLoading &&
                    loadingAction === ExceptionReviewAction.Rejected ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CloseIcon fontSize="inherit" />
                    )
                  }
                  disabled={isLoading}
                  onClick={() => {
                    onReviewActionChange(
                      exceptionGroup,
                      ExceptionReviewAction.Rejected,
                    );
                  }}
                >
                  Reject
                </Button>
              )
            )}
            {canApplyExceptionUpdate && onReviewActionChange && (
              <ExceptionButtonSelfApprovalTooltipWrapper
                exceptionsAllowSelfApproval={
                  exceptionsAllowSelfApproval || !createdBySelf
                }
              >
                <Button
                  variant="outlined"
                  color="excellent"
                  data-test-id="exception-approve-button"
                  startIcon={
                    isLoading &&
                    loadingAction === ExceptionReviewAction.Approved ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckIcon fontSize="inherit" />
                    )
                  }
                  disabled={
                    isLoading || (!exceptionsAllowSelfApproval && createdBySelf)
                  }
                  onClick={() => {
                    onReviewActionChange(
                      exceptionGroup,
                      ExceptionReviewAction.Approved,
                    );
                  }}
                >
                  Approve
                </Button>
              </ExceptionButtonSelfApprovalTooltipWrapper>
            )}
          </Flex>
        )}
    </>
  );
};

export default ExceptionButtons;
